import { createSlice } from '@reduxjs/toolkit';
import {
  getAllCandidate,
  getCandidateAssessment,
  getCandidateData,
  getCandidateOverview,
  updateCandidateMainInfo,
  createCandidate,
  deleteCandidate,
  editCandidate
} from 'requests/candidate';
import { ID } from 'requests';
import { AppDispatch, RootState } from 'redux/store';
import {
  initialAllCandidates,
  initialAssessmentRes,
  initialCandidate
} from 'constants/states';
import { enqueueSnackbar } from './notifications';
import { LIMIT } from '../../constants/constants';

const initialState = {
  allCandidates: initialAllCandidates,
  candidateLoading: false,
  candidate: initialCandidate,
  talentFilters: {
    currentPage: 1,
    currentRole: 'all_candidates'
  },
  assessmentLoading: false,
  assessmentResult: initialAssessmentRes,
  soft_skills: [],
  hard_skills: [],
  skillsError: null,
  skillsStreaming: false,
  skillsProgress: {
    hard_skills: [],
    soft_skills: [],
    extracted: 0,
    total: 0
  }
};

const slice = createSlice({
  name: 'candidateSlice',
  initialState,
  reducers: {
    setAllCandidates(state, action) {
      state.allCandidates = action.payload;
    },
    candidateProfileLoading(state, action) {
      state.candidateLoading = action.payload;
    },
    assessmentProfileLoading(state, action) {
      state.assessmentLoading = action.payload;
    },
    resetProfileSuccess(state) {
      state.candidate = initialCandidate;
      state.assessmentResult = initialAssessmentRes;
    },
    candidateDataSuccess(state, action) {
      state.candidate = {
        ...state.candidate,
        ...action.payload
      };
    },
    changeFilter(state, action) {
      state.talentFilters = {
        ...state.talentFilters,
        ...action.payload
      };
    },
    candidateAllSuccess(state, action) {
      state.candidate = {
        ...state.candidate,
        ...action.payload
      };
    },
    candidateAssessmentResultSuccess(state, action) {
      state.assessmentResult = action.payload.assessmentResult;
    },
    setSoftSkills(state, action) {
      state.soft_skills = action.payload;
    },
    setHardSkills(state, action) {
      state.hard_skills = action.payload;
    },
    setSkillsError(state, action) {
      state.skillsError = action.payload;
    },
    setSkillsStreaming(state, action) {
      state.skillsStreaming = action.payload;
    },
    updateSkillsProgress(state, action) {
      state.skillsProgress = {
        ...state.skillsProgress,
        ...action.payload
      };
    },
    resetSkillsProgress(state) {
      state.skillsProgress = {
        hard_skills: [],
        soft_skills: [],
        extracted: 0,
        total: 0
      };
    },
    setStreamingError(state, action) {
      state.skillsError = action.payload;
      state.skillsStreaming = false;
    }
  }
});

export default slice.reducer;

export const setCandidateLoading =
  (isLoading: boolean) => (dispatch: AppDispatch) =>
    dispatch(slice.actions.candidateProfileLoading(isLoading));

export const setAssessmentLoading =
  (isLoading: boolean) => (dispatch: AppDispatch) =>
    dispatch(slice.actions.assessmentProfileLoading(isLoading));

export const resetCandidate = () => (dispatch: AppDispatch) =>
  dispatch(slice.actions.resetProfileSuccess());

export const getAllCandidateData =
  (id: ID, modules: any) => async (dispatch: AppDispatch) => {
    dispatch(setCandidateLoading(true));
    try {
      await dispatch(getCandidateInfo(id));
      modules.work_experience &&
        (await dispatch(getCandidateWorkExperience(id)));
      modules.education && (await dispatch(getCandidateEducations(id)));
      await dispatch(getCandidateFiles(id));
      modules.notes && (await dispatch(getCandidateNotes(id)));
      modules.language && (await dispatch(getCandidateLanguages(id)));
      modules.extra && (await dispatch(getCandidateActivities(id)));
      modules.certification && (await dispatch(getCandidateCertifications(id)));
      modules.it_skill && (await dispatch(getCandidateITSkills(id)));
      modules.social_media && (await dispatch(getCandidateSocialMedia(id)));
      modules.social_media_expertise &&
        (await dispatch(getCandidateSocialMediaExpertises(id)));
      modules.preference && (await dispatch(getCandidateIndustries(id)));
    } catch {
      dispatch(
        enqueueSnackbar({
          messageType: 'error',
          options: { variant: 'error' }
        })
      );
    } finally {
      dispatch(setCandidateLoading(false));
    }
  };

export const getCandidateInfo = (id: ID) => async (dispatch: AppDispatch) =>
  getCandidateOverview(id)
    .then((res) => {
      dispatch(slice.actions.candidateDataSuccess({ mainInfo: res }));
    })
    .catch(() =>
      dispatch(
        enqueueSnackbar({
          messageType: 'error',
          options: { variant: 'error' }
        })
      )
    );

export const getCandidateWorkExperience =
  (id: ID) => async (dispatch: AppDispatch) =>
    getCandidateData(id, 'work-experiences')
      .then((res) =>
        dispatch(
          slice.actions.candidateDataSuccess({
            work_experience: res
          })
        )
      )
      .catch(() =>
        dispatch(
          enqueueSnackbar({
            messageType: 'error',
            options: { variant: 'error' }
          })
        )
      );

export const getCandidateEducations =
  (id: ID) => async (dispatch: AppDispatch) =>
    getCandidateData(id, 'educations')
      .then((res) =>
        dispatch(
          slice.actions.candidateDataSuccess({
            educations: res
          })
        )
      )
      .catch(() =>
        dispatch(
          enqueueSnackbar({
            messageType: 'error',
            options: { variant: 'error' }
          })
        )
      );

export const getCandidateFiles = (id: ID) => async (dispatch: AppDispatch) =>
  getCandidateData(id, 'files')
    .then((res) =>
      dispatch(
        slice.actions.candidateDataSuccess({
          files: res
        })
      )
    )
    .catch(() =>
      dispatch(
        enqueueSnackbar({
          messageType: 'error',
          options: { variant: 'error' }
        })
      )
    );

export const getCandidateNotes = (id: ID) => async (dispatch: AppDispatch) =>
  getCandidateData(id, 'notes')
    .then((res) => {
      dispatch(
        slice.actions.candidateDataSuccess({
          notes: res
        })
      );
    })
    .catch(() =>
      dispatch(
        enqueueSnackbar({
          messageType: 'error',
          options: { variant: 'error' }
        })
      )
    );

export const getCandidateLanguages =
  (id: ID) => async (dispatch: AppDispatch) =>
    getCandidateData(id, 'languages')
      .then((res) =>
        dispatch(
          slice.actions.candidateDataSuccess({
            languages: res
          })
        )
      )
      .catch(() =>
        dispatch(
          enqueueSnackbar({
            messageType: 'error',
            options: { variant: 'error' }
          })
        )
      );

export const getCandidateActivities =
  (id: ID) => async (dispatch: AppDispatch) =>
    getCandidateData(id, 'activities')
      .then((res) =>
        dispatch(
          slice.actions.candidateDataSuccess({
            activities: res
          })
        )
      )
      .catch(() =>
        dispatch(
          enqueueSnackbar({
            messageType: 'error',
            options: { variant: 'error' }
          })
        )
      );

export const getCandidateCertifications =
  (id: ID) => async (dispatch: AppDispatch) =>
    getCandidateData(id, 'certifications')
      .then((res) =>
        dispatch(
          slice.actions.candidateDataSuccess({
            certifications: res
          })
        )
      )
      .catch(() =>
        dispatch(
          enqueueSnackbar({
            messageType: 'error',
            options: { variant: 'error' }
          })
        )
      );

export const getCandidateITSkills = (id: ID) => async (dispatch: AppDispatch) =>
  getCandidateData(id, 'it-skills')
    .then((res) =>
      dispatch(
        slice.actions.candidateDataSuccess({
          it_skill: res
        })
      )
    )
    .catch(() =>
      dispatch(
        enqueueSnackbar({
          messageType: 'error',
          options: { variant: 'error' }
        })
      )
    );

export const getCandidateSocialMedia =
  (id: ID) => async (dispatch: AppDispatch) =>
    getCandidateData(id, 'social-media')
      .then((res) =>
        dispatch(
          slice.actions.candidateDataSuccess({
            social_media: res
          })
        )
      )
      .catch(() =>
        dispatch(
          enqueueSnackbar({
            messageType: 'error',
            options: { variant: 'error' }
          })
        )
      );

export const getCandidateSocialMediaExpertises =
  (id: ID) => async (dispatch: AppDispatch) =>
    getCandidateData(id, 'social-media-expertises')
      .then((res) =>
        dispatch(
          slice.actions.candidateDataSuccess({
            social_media_expertise: res
          })
        )
      )
      .catch(() =>
        dispatch(
          enqueueSnackbar({
            messageType: 'error',
            options: { variant: 'error' }
          })
        )
      );

export const getCandidateIndustries =
  (id: ID) => async (dispatch: AppDispatch) =>
    getCandidateData(id, 'industries')
      .then((res) =>
        dispatch(
          slice.actions.candidateDataSuccess({
            industries: res
          })
        )
      )
      .catch(() =>
        dispatch(
          enqueueSnackbar({
            messageType: 'error',
            options: { variant: 'error' }
          })
        )
      );

export const selectSkillsError = (state: RootState) =>
  state.candidate.skillsError;

export const selectSkillsStreaming = (state: RootState) =>
  state.candidate.skillsStreaming;

export const selectSkillsProgress = (state: RootState) =>
  state.candidate.skillsProgress;

export const retrySkillsExtraction = (id: ID, formData: any) =>
  (dispatch: AppDispatch) => {
    dispatch(slice.actions.setSkillsError(null));
    dispatch(patchCandidateMainInfoData(id, formData));
  };

export const patchCandidateMainInfoData =
  (id: ID, formData: any, onProgress?: (skills: any) => void) =>
  (dispatch: AppDispatch, getState: () => RootState) => {
    const { skillsError } = getState().candidate;
    dispatch(slice.actions.setHardSkills([]));
    dispatch(slice.actions.setSoftSkills([]));
    dispatch(slice.actions.resetSkillsProgress());
    dispatch(slice.actions.setSkillsStreaming(true));
    dispatch(slice.actions.setSkillsError(null));
    
    // Set a timeout to prevent infinite streaming (5 minutes)
    const timeoutId = setTimeout(() => {
      dispatch(slice.actions.setStreamingError('Skills extraction timed out. Please try again.'));
    }, 5 * 60 * 1000);
    
    updateCandidateMainInfo(id, formData, (streamingData) => {
      try {
        // Handle streaming skills updates
        if (streamingData.skills) {
          const { hard_skills, soft_skills, extracted, total } = streamingData.skills;
          
          dispatch(slice.actions.updateSkillsProgress({
            hard_skills: hard_skills || [],
            soft_skills: soft_skills || [],
            extracted: extracted || 0,
            total: total || 0
          }));
          
          // Update skills in real-time
          if (hard_skills) {
            dispatch(slice.actions.setHardSkills(hard_skills));
          }
          if (soft_skills) {
            dispatch(slice.actions.setSoftSkills(soft_skills));
          }
          
          // Call progress callback if provided
          if (onProgress) {
            onProgress(streamingData.skills);
          }
        }
        
        // Handle streaming errors
        if (streamingData.error) {
          dispatch(slice.actions.setStreamingError(streamingData.error));
        }
      } catch (error) {
        console.error('Error processing streaming data:', error);
        dispatch(slice.actions.setStreamingError('Failed to process streaming data.'));
      }
    })
      .then((res) => {
        clearTimeout(timeoutId);
        dispatch(slice.actions.setSkillsStreaming(false));
        dispatch(
          slice.actions.candidateDataSuccess({
            mainInfo: res
          })
        );
        
        // Handle final skills state
        if (res.hard_skills?.length > 0 || res.soft_skills?.length > 0) {
          dispatch(slice.actions.setSkillsError(null));
          dispatch(slice.actions.setHardSkills(res.hard_skills || []));
          dispatch(slice.actions.setSoftSkills(res.soft_skills || []));
        } else if (res.error) {
          dispatch(
            slice.actions.setSkillsError(
              'Failed to fetch skills from uploaded CV.'
            )
          );
        }
        
        if (!skillsError) {
          dispatch(
            enqueueSnackbar({
              messageType: 'updated',
              options: { variant: 'success' }
            })
          );
        }
      })
      .catch((error) => {
        clearTimeout(timeoutId);
        dispatch(slice.actions.setSkillsStreaming(false));
        dispatch(slice.actions.setSkillsError('Failed to process CV upload.'));
        dispatch(
          enqueueSnackbar({
            messageType: 'error',
            options: { variant: 'error' }
          })
        );
      });
  };

export const getCandidateAssessmentResults =
  (id: ID) => async (dispatch: AppDispatch) => {
    dispatch(setAssessmentLoading(true));
    getCandidateAssessment(id)
      .then((res) =>
        dispatch(
          slice.actions?.candidateAssessmentResultSuccess({
            assessmentResult: Object.keys(res).length
              ? res
              : initialAssessmentRes
          })
        )
      )
      .catch(() =>
        dispatch(
          enqueueSnackbar({
            messageType: 'error',
            options: { variant: 'error' }
          })
        )
      )
      .finally(() => dispatch(setAssessmentLoading(false)));
  };

export const getAllUserAndAdmin =
  (id: ID, role: string, q: string, currentPage: number) =>
  async (dispatch: AppDispatch) =>
    getAllCandidate(id, role, q, currentPage, LIMIT)
      .then((res) => {
        dispatch(slice.actions.setAllCandidates(res));
      })
      .catch(() =>
        dispatch(
          enqueueSnackbar({
            messageType: 'error',
            options: { variant: 'error' }
          })
        )
      );

export const changeFilters =
  (fieldName: string, value: any) => async (dispatch: AppDispatch) => {
    try {
      dispatch(
        slice.actions.changeFilter({
          [fieldName]: value
        })
      );
    } catch {
      dispatch(
        enqueueSnackbar({
          messageType: 'error',
          options: { variant: 'error' }
        })
      );
    }
  };

export const createNewCandidate =
  (data: any) => async (dispatch: AppDispatch) => {
    try {
      createCandidate(data).then(() =>
        dispatch(
          enqueueSnackbar({
            messageType: 'updated',
            options: { variant: 'success' }
          })
        )
      );
    } catch {
      dispatch(
        enqueueSnackbar({
          messageType: 'error',
          options: { variant: 'error' }
        })
      );
    }
  };

export const editInactiveCandidate =
  (data: any, candidate_id: any) => async (dispatch: AppDispatch) => {
    try {
      editCandidate(data, candidate_id).then(() =>
        dispatch(
          enqueueSnackbar({
            messageType: 'updated',
            options: { variant: 'success' }
          })
        )
      );
    } catch {
      dispatch(
        enqueueSnackbar({
          messageType: 'error',
          options: { variant: 'error' }
        })
      );
    }
  };

export const deleteInactiveCandidate =
  (candidate_id: any) => async (dispatch: AppDispatch) => {
    try {
      deleteCandidate(candidate_id).then(() =>
        dispatch(
          enqueueSnackbar({
            messageType: 'deleted',
            options: { variant: 'success' }
          })
        )
      );
    } catch {
      dispatch(
        enqueueSnackbar({
          messageType: 'error',
          options: { variant: 'error' }
        })
      );
    }
  };